import React, { useEffect, useState, useCallback } from 'react';
import api from '../config/axios';

type Role = 'administrador' | 'supervisor' | 'backoffice' | 'vendedor';
interface User { email: string; name: string; document_number?: string; role: Role; is_active: boolean; created_at: string; }

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<Role | ''>('');
  const [state, setState] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [form, setForm] = useState<{email: string; name: string; document_number: string; role: Role; is_active: boolean; password: string}>({
    email: '', name: '', document_number: '', role: 'vendedor', is_active: true, password: ''
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (role) params.role = role;
      const { data } = await api.get('/users', { params });
      setUsers(data || []);
      setError('');
    } catch {
      setError('Error al cargar usuarios');
    } finally { setLoading(false); }
  }, [role]);

  useEffect(()=>{ load(); }, [role, load]);

  const filtered = users.filter(u => {
    const hit = u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const st = state === '' ? true : (state === 'activos' ? u.is_active : !u.is_active);
    return hit && st;
  });

  const openNew = () => {
    setEditing(null);
    setForm({ email: '', name: '', document_number: '', role: 'vendedor', is_active: true, password: '' });
    setShow(true);
  };
  const openEdit = (u: User) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name, document_number: u.document_number || '', role: u.role, is_active: u.is_active, password: '' });
    setShow(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editing) {
        const body: any = { name: form.name, document_number: form.document_number, role: form.role, is_active: form.is_active };
        if (form.password) body.password = form.password; // cambiar clave solo si se envía
        await api.put(`/users/${editing.email}`, body);
      } else {
        await api.post('/users', form);
      }
      setShow(false);
      await load();
    } catch (err: any) {
      if (err?.response?.status === 409) setError('El usuario ya existe');
      else setError('Error al guardar usuario');
    } finally { setLoading(false); }
  };

  const toggle = async (u: User) => {
    try {
      await api.put(`/users/${u.email}`, { is_active: !u.is_active });
      load();
    } catch { setError('Error al actualizar estado'); }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Usuarios</h2>
        <button className="btn btn-primary" onClick={openNew}>Nuevo Usuario</button>
      </div>

      <div className="card mb-3">
        <div className="card-body d-flex gap-2 flex-wrap">
          <input className="form-control" style={{maxWidth:360}} placeholder="Buscar por nombre o email" value={search} onChange={e=>setSearch(e.target.value)} />
          <select className="form-select" style={{maxWidth:220}} value={role} onChange={e=>setRole(e.target.value as any)}>
            <option value="">Rol (todos)</option>
            <option value="administrador">Administrador</option>
            <option value="supervisor">Supervisor</option>
            <option value="backoffice">Backoffice</option>
            <option value="vendedor">Vendedor</option>
          </select>
          <select className="form-select" style={{maxWidth:200}} value={state} onChange={e=>setState(e.target.value)}>
            <option value="">Estado (todos)</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>
      </div>

      {error && (<div className="alert alert-danger">{error}</div>)}

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-dark">
                <tr>
                  <th>Nombre</th><th>Email</th><th>Documento</th><th>Rol</th><th>Estado</th><th>Creado</th><th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.email}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{u.document_number || '-'}</td>
                    <td><span className="badge bg-secondary">{u.role}</span></td>
                    <td><span className={`badge ${u.is_active?'bg-success':'bg-secondary'}`}>{u.is_active?'Activo':'Inactivo'}</span></td>
                    <td>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="btn-group">
                        <button className="btn btn-sm btn-outline-primary" onClick={()=>openEdit(u)}>Editar</button>
                        <button className={`btn btn-sm ${u.is_active?'btn-outline-warning':'btn-outline-success'}`} onClick={()=>toggle(u)}>
                          {u.is_active?'Desactivar':'Activar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {show && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editing?'Editar Usuario':'Nuevo Usuario'}</h5>
                <button type="button" className="btn-close" onClick={()=>setShow(false)}></button>
              </div>
              <form onSubmit={submit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required disabled={!!editing} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Nombre</label>
                    <input type="text" className="form-control" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Número de Documento</label>
                    <input type="text" className="form-control" value={form.document_number} onChange={e=>setForm({...form,document_number:e.target.value})} placeholder="Ej: 12345678, ABC123456" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Rol</label>
                    <select className="form-select" value={form.role} onChange={e=>setForm({...form,role:e.target.value as Role})}>
                      <option value="administrador">Administrador</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="backoffice">Backoffice</option>
                      <option value="vendedor">Vendedor</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Contraseña {editing && <small className="text-muted">(dejar vacío para no cambiar)</small>}</label>
                    <input type="password" className="form-control" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} required={!editing} minLength={6} />
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={form.is_active} onChange={e=>setForm({...form,is_active:e.target.checked})} id="activeCheck" />
                    <label className="form-check-label" htmlFor="activeCheck">Usuario activo</label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={()=>setShow(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Guardando...':editing?'Actualizar':'Crear'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;


